/**
 * Per-email desktop preview width.
 *
 * The canvas iframe's viewport IS the "device": the email's own `@media`
 * rules resolve at whatever width the email node renders at. Desktop can't be
 * one fixed width — emails carry mobile breakpoints anywhere up to
 * MAX_MOBILE_BREAKPOINT_WIDTH (1024px; imported emails bring arbitrary ones),
 * so a fixed viewport at or below an email's breakpoint renders its MOBILE
 * styles on the desktop canvas (stacked columns in a "desktop" preview).
 * Rendering everything above every possible breakpoint instead (the
 * device-group pass's DESKTOP_EVAL_WIDTH_PX) pads the typical 600px email
 * with hundreds of pixels of empty node.
 *
 * So the desktop viewport is derived per email, as the widest of two signals,
 * never narrower than the EMAIL_WIDTH default:
 * - the narrowest width that puts every width-switched `@media` rule the email
 *   carries in its desktop state, and
 * - the widest fixed `<table>` the email lays out — table-based emails declare
 *   their desktop design width that way (`width="700"`), and a viewport under
 *   it clips the desktop layout even once every media query resolves desktop.
 * An email with neither signal can't change layout with the viewport, so the
 * default is exact for it.
 */
import { MAX_MOBILE_BREAKPOINT_WIDTH } from "@hightouch/lib/brand-profiles/settings";

import { EMAIL_WIDTH } from "src/pages/lifecycle-studio/emails/email-editor/canvas/dynamic-content/branch-srcdoc";
import {
  collectMediaConditions,
  collectStyleSheets,
} from "src/pages/lifecycle-studio/emails/email-editor/canvas/email-head-styles";

/** Same clause shape `mediaConditionAppliesAtWidth` evaluates — only px width
 * clauses can be turned into a required viewport. */
const WIDTH_CLAUSE = /\(\s*(min|max)-(?:device-)?width\s*:\s*([\d.]+)px\s*\)/gi;

/**
 * The widest fixed `<table>` in the document (`width` attribute or an inline
 * px width), or null when there is none. Only tables: they're how table-based
 * emails state their design width, while a wide `<img>` is routinely reined in
 * by `max-width: 100%`. Widths above MAX_MOBILE_BREAKPOINT_WIDTH are ignored,
 * like the media bounds below — nothing wider is a real design width.
 */
export const maxFixedTableWidth = (root: ParentNode): number | null => {
  let max: number | null = null;
  for (const table of root.querySelectorAll("table")) {
    const attr = table.getAttribute("width") ?? "";
    const styleWidth = table.style.width;
    for (const candidate of [
      /^\s*([\d.]+)\s*$/.exec(attr)?.[1],
      /^\s*([\d.]+)px\s*$/.exec(styleWidth)?.[1],
    ]) {
      if (candidate === undefined) continue;
      const width = Math.ceil(parseFloat(candidate));
      if (!Number.isFinite(width) || width > MAX_MOBILE_BREAKPOINT_WIDTH) {
        continue;
      }
      if (max === null || width > max) max = width;
    }
  }
  return max;
};

/**
 * The desktop viewport width for an email: the narrowest width that is
 * strictly above every `max-width` bound and at least every `min-width` bound
 * its media rules switch on, AND fits its widest fixed table — floored at
 * EMAIL_WIDTH.
 *
 * Bounds above MAX_MOBILE_BREAKPOINT_WIDTH are ignored: nothing that wide is
 * a form-factor split (client-targeting hacks like Gmail's
 * `@media screen and (max-width: 9999px)` live up there), and honoring them
 * would drag every such email to the ceiling.
 */
export const computeDesktopPreviewWidth = ({
  cssTexts,
  fixedLayoutWidth,
}: {
  cssTexts: readonly string[];
  /** Widest fixed table (see `maxFixedTableWidth`) — null when none/unknown. */
  fixedLayoutWidth?: number | null;
}): number => {
  const conditions = cssTexts.flatMap((css) => collectMediaConditions(css));
  let width = Math.max(EMAIL_WIDTH, fixedLayoutWidth ?? 0);
  for (const condition of conditions) {
    for (const clause of condition.matchAll(WIDTH_CLAUSE)) {
      const bound = parseFloat(clause[2] ?? "");
      if (!Number.isFinite(bound) || bound > MAX_MOBILE_BREAKPOINT_WIDTH) {
        continue;
      }
      // Above a max-width bound means strictly above it; a min-width bound is
      // itself satisfying.
      const needed =
        clause[1]?.toLowerCase() === "max"
          ? Math.floor(bound) + 1
          : Math.ceil(bound);
      width = Math.max(width, needed);
    }
  }
  return width;
};

/** `computeDesktopPreviewWidth` from a raw email string (one DOMParser pass) —
 * for surfaces that don't already hold the parsed email. */
export const computeDesktopPreviewWidthFromHtml = (html: string): number => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return computeDesktopPreviewWidth({
    cssTexts: collectStyleSheets(doc.head),
    fixedLayoutWidth: maxFixedTableWidth(doc),
  });
};
